<style>
@import '@/assets/css/custom.css';
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Bank transactions" />

		<div class="container-fluid">
			<div class="row">
				<div class="col-sm-12">
					<div class="card">
						<div class="card-body">
							<div class="row mb-3">
								<div class="col-12">
									<router-link :to="{ name: 'bank_accounts' }" class="btn btn-primary float-end">
										<i class="bx bx-chevrons-left"></i> Back to bank accounts
									</router-link>
								</div>
							</div>

							<div class="alert alert-light border mb-3 d-flex flex-wrap gap-3" v-if="bankAccount || bank_account_id">
								<template v-if="bankAccount">
									<div><strong>Bank name:</strong> {{ bankAccount.bank_name || "—" }}</div>
									<div><strong>Account no.:</strong> {{ bankAccount.account_number || "—" }}</div>
									<div v-if="bankAccount.ifsc_code"><strong>IFSC:</strong> {{ bankAccount.ifsc_code }}</div>
									<div v-if="bankAccount.branch"><strong>Branch:</strong> {{ bankAccount.branch }}</div>
									<!-- <div>
										<strong>Status:</strong>
										<span class="badge rounded-pill bg-success ms-1" v-if="bankAccount.status == 1">Active</span>
										<span class="badge rounded-pill bg-secondary ms-1" v-else>Inactive</span>
									</div> -->
								</template>
								<div v-else><strong>Bank account ID:</strong> {{ bank_account_id }}</div>
							</div>

							<div class="col-md-12 project-list filter-sort">
								<div class="row align-items-end">
									<div class="col-md-3 d-flex mt-2">
										<label class="d-flex listing-length ms-1 align-items-center">
											<span>Show</span>
											<select class="form-select mx-2" style="width: auto" v-model="listing_length" @change="fetchTransactions()">
												<option value="5">5</option>
												<option value="10">10</option>
												<option value="20">20</option>
											</select>
											<span>entries</span>
										</label>
									</div>
									<div class="col-md-2 mt-2">
										<label class="form-label small mb-1">From date</label>
										<input
											type="date"
											class="form-control"
											v-model="from_date"
											@change="fetchTransactions()"
										/>
									</div>
									<div class="col-md-2 mt-2">
										<label class="form-label small mb-1">To date</label>
										<input
											type="date"
											class="form-control"
											v-model="to_date"
											@change="fetchTransactions()"
										/>
									</div>
									<div class="col-md-2 mt-2">
										<select class="form-select" v-model="filter_type" @change="fetchTransactions()">
											<option value="">All types</option>
											<option value="1">Renewal/Service Payment</option>
											<option value="2">Vendor Payment</option>
											<option value="3">Courier Payment</option>
										</select>
									</div>
									<div class="col-md-3 mt-2">
										<input
											type="text"
											class="form-control"
											placeholder="Search…"
											v-model="keyword"
											@input="fetchTransactions()"
										/>
									</div>
								</div>
							</div>

							<div
								v-if="opening_balance != null || closing_balance != null"
								class="d-flex justify-content-end align-items-end gap-4 mt-2 mb-1 text-end"
							>
								<div v-if="opening_balance != null">
									<div class="small text-muted">Opening</div>
									<div class="fw-bold">₹{{ formatMoney(opening_balance) }}</div>
								</div>
								<div v-if="closing_balance != null">
									<div class="small text-muted">Closing</div>
									<div class="fw-bold">₹{{ formatMoney(closing_balance) }}</div>
								</div>
							</div>

							<div class="table-responsive product-table mt-3">
								<table class="display table">
									<thead>
										<tr align="center">
											<th>Sl.No</th>
											<th>Type</th>
											<th>Transaction ID</th>
											<th>Dr / Cr</th>
											<th>Amount</th>
											<th>Transaction on</th>
											<th>Payment By</th>
											<th>Opening</th>
											<th>Closing</th>
											<th>Verified</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody v-if="rows.length">
										<tr align="center" v-for="(row, index) in rows" :key="row.id">
											<td>{{ from != null ? from + index : index + 1 }}</td>
											<td>
												<span class="badge rounded-pill bg-info text-dark">{{ categoryTypeLabel(row.type) }}</span><br>
												<span v-if="row.imei"><strong>IMEI:</strong> {{ row.imei }} </span>
											</td>
											<td class="text-start">{{ row.transaction_id || "—" }} <br>
												<span v-if="row.added_by_user_name"><strong>Payment added by:</strong> {{ row.added_by_user_name }} </span>
											</td>
											<td>
												<span class="badge rounded-pill" :class="transactionTypeClass(row.transaction_type)">
													{{ transactionTypeLabel(row.transaction_type) }}
												</span>
											</td>
											<td>₹{{ formatMoney(row.transaction_amount) }}<br>
												<span v-if="row.remarks != null"><strong>Remark:</strong> {{ row.remarks }} </span>
											</td>
											<td>{{ row.payment_date ? $filters.formatDate(row.payment_date) : (row.transaction_on ? $filters.formatDate(row.transaction_on) : "—") }}</td>
											<td>
												<span v-if="row.payment_user_type == 1"><strong>Payment by Dealer:</strong> {{ row.payment_user_name }} </span>
												<span v-else-if="row.payment_user_type == 2"><strong>Payment by Customer:</strong> {{ row.payment_user_name }} </span>
												<span v-else>—</span>
											</td>
											<td class="fw-bold">₹{{ formatMoney(row.opening_balance) }}</td>
											<td class="fw-bold">₹{{ formatMoney(row.closing_balance) }}</td>
											<td>
												<span class="badge rounded-pill bg-success" v-if="row.is_verified == 1">Yes</span>
												<span class="badge rounded-pill bg-secondary" v-else-if="row.is_verified == 2">No</span>
												<span v-else>—</span>
												<br><span v-if="row.is_verified == 1"> {{ row.verified_on ? $filters.formatDateTime(row.verified_on) : "—" }} </span>
												<br><span v-if="row.verified_by_user_name"> <strong>{{ row.verified_by_user_name }}</strong> </span>
												<br><span v-if="row.deleted_at" class="badge rounded-pill bg-danger"> Revoked</span>
											</td>
											<td>
												<div class="d-flex flex-column align-items-center gap-1">
													<button
														v-if="row.is_verified == 2"
														type="button"
														class="btn btn-sm btn-success"
														:disabled="verifyingId === row.id"
														@click="openVerifyModal(row)"
													>
														<span v-if="verifyingId === row.id" class="spinner-border spinner-border-sm me-1"></span>
														Verify
													</button>
													<button
														type="button"
														class="btn btn-sm btn-outline-danger"
														:disabled="revokeModal.submitting && revokeModal.row?.id === row.id"
														@click="openRevokeModal(row)"
														v-if="row.deleted_at == null && $canRevoke()"
													>
														<span v-if="revokeModal.submitting && revokeModal.row?.id === row.id" class="spinner-border spinner-border-sm me-1"></span>
														Revoke
													</button>

													<br><span v-if="row.deleted_at"> Revoked on: {{ row.deleted_at ? $filters.formatDateTime(row.deleted_at) : "—" }} </span>
													<br><span v-if="row.deleted_by_user_name"> Revoked by: <strong>{{ row.deleted_by_user_name }}</strong> </span>
												</div>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="11" align="center">No transactions found</td>
										</tr>
									</tbody>
								</table>
							</div>

							<caption v-if="rows.length" class="float-start mt-2 d-block">
								Showing {{ from }} to {{ to }} of {{ total }} entries
							</caption>
							<nav v-if="rows.length && pagination.length" aria-label="Page navigation" class="mt-2 float-end">
								<ul class="pagination mb-0">
									<li
										class="page-item"
										v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
										v-for="(page, index) in pagination"
										:key="'p-' + index + '-' + page.label"
									>
										<a class="page-link" href="#" @click.prevent="onPageClick(page, index)" tabindex="-1">
											<span v-if="index === 0">Prev</span>
											<span v-else-if="index === pagination.length - 1">Next</span>
											<span v-else>{{ page.label }}</span>
										</a>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Verify transaction modal -->
		<div
			v-if="verifyModal.open && verifyModal.row"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			style="background-color: rgba(0, 0, 0, 0.5)"
			@click.self="closeVerifyModal"
		>
			<div class="modal-dialog" @click.stop>
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Verify transaction</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closeVerifyModal"></button>
					</div>
					<form @submit.prevent="submitVerify">
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Type:</strong> {{ categoryTypeLabel(verifyModal.row.type) }}</div>
								<div><strong>Amount:</strong> ₹{{ formatMoney(verifyModal.row.transaction_amount) }}</div>
								<div v-if="verifyModal.row.payment_date">
									<strong>Transaction on:</strong> {{ $filters.formatDate(verifyModal.row.payment_date) }}
								</div>
							</div>
							<div class="form-group">
								<label class="control-label">Payment / transaction ID</label>
								<input
									class="form-control"
									type="text"
									v-model="verifyModal.payment_id"
									placeholder="UTR / reference (optional)"
								/>
								<small class="text-muted d-block mt-1">
									{{
										verifyModal.row.payment_id
											? "Existing ID is shown — edit if needed."
											: "No payment ID on record — enter one if available."
									}}
								</small>
								<div v-if="verifyModal.errors.payment_id" class="text-danger small mt-1">
									{{ verifyModal.errors.payment_id }}
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" :disabled="verifyingId === verifyModal.row.id" @click="closeVerifyModal">
								Cancel
							</button>
							<button type="submit" class="btn btn-success" :disabled="verifyingId === verifyModal.row.id">
								<span v-if="verifyingId === verifyModal.row.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
								{{ verifyingId === verifyModal.row.id ? "Saving..." : "Verify" }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Revoke transaction modal -->
		<div
			v-if="revokeModal.open && revokeModal.row"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			style="background-color: rgba(0, 0, 0, 0.5)"
			@click.self="closeRevokeModal"
		>
			<div class="modal-dialog modal-dialog-scrollable" @click.stop>
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Revoke transaction</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closeRevokeModal"></button>
					</div>
					<div class="modal-body">
						<p class="mb-3">Are you sure you want to revoke this bank transaction?</p>
						<div class="small border rounded p-3 bg-light text-dark">
							<div class="mb-1"><strong>Transaction ID:</strong> #{{ revokeModal.row.id }}</div>
							<div class="mb-1"><strong>Type:</strong> {{ categoryTypeLabel(revokeModal.row.type) }}</div>
							<div class="mb-1"><strong>Dr / Cr:</strong> {{ transactionTypeLabel(revokeModal.row.transaction_type) }}</div>
							<div class="mb-1"><strong>Amount:</strong> ₹{{ formatMoney(revokeModal.row.transaction_amount) }}</div>
							<div class="mb-1"><strong>Payment ID:</strong> {{ revokeModal.row.payment_id || "—" }}</div>
							<div class="mb-1"><strong>Transaction on:</strong> {{ revokeModal.row.payment_date ? $filters.formatDate(revokeModal.row.payment_date) : "—" }}</div>
							<div class="mb-1"><strong>Closing balance:</strong> ₹{{ formatMoney(revokeModal.row.closing_balance) }}</div>
							<div class="mb-1"><strong>Verified:</strong> {{ revokeModal.row.is_verified == 1 ? "Yes" : revokeModal.row.is_verified == 2 ? "No" : "—" }}</div>
							<div class="mb-0"><strong>Verified on:</strong> {{ revokeModal.row.verified_on ? $filters.formatDate(revokeModal.row.verified_on) : "—" }}</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-danger" :disabled="revokeModal.submitting" @click="confirmRevokeBankTransaction">
							<span v-if="revokeModal.submitting" class="spinner-border spinner-border-sm me-2"></span>
							Yes, revoke
						</button>
						<button type="button" class="btn btn-light" :disabled="revokeModal.submitting" @click="closeRevokeModal">No</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "BankAccountTransactions",
	data() {
		return {
			token: "",
			bank_account_id: "",
			listing_length: 10,
			filter_type: "",
			keyword: "",
			from_date: "",
			to_date: new Date().toISOString().slice(0, 10),
			bankAccount: null,
			opening_balance: null,
			closing_balance: null,
			rows: [],
			pagination: [],
			from: 0,
			to: 0,
			total: 0,
			active_page_url: "",
			verifyingId: null,
			verifyModal: {
				open: false,
				row: null,
				payment_id: "",
				errors: {},
			},
			revokeModal: {
				open: false,
				row: null,
				submitting: false,
			},
		};
	},
	created() {
		this.bank_account_id = this.$route.params.bank_id || "";
		if (!this.bank_account_id) {
			this.$swal("Error", "Bank account not specified", "error");
			this.$router.push({ name: "bank_accounts" });
			return;
		}
		this.fetchTransactions();
	},
	watch: {
		"$route.params.bank_id"(val) {
			this.bank_account_id = val || "";
			if (this.bank_account_id) {
				this.fetchTransactions();
			}
		},
	},
	methods: {
		getToken() {
			const raw = localStorage.getItem("airotrack_staff");
			if (!raw) {
				this.$swal("", "Session expired, please login again", "error");
				this.$router.push({ name: "login" });
				return;
			}
			const airotrack_staff = JSON.parse(raw);
			if (!airotrack_staff.token) {
				this.$swal("", "Failed to fetch token, please login again", "error");
				this.$router.push({ name: "login" });
				return;
			}
			this.token = airotrack_staff.token;
		},

		querySuffix() {
			const typeParam =
				this.filter_type !== "" && this.filter_type != null ? String(this.filter_type) : "";
			const params = new URLSearchParams({
				bank_account_id: String(this.bank_account_id),
				limit: String(this.listing_length),
				keyword: this.keyword || "",
				type: typeParam,
			});
			if (this.from_date) {
				params.set("from_date", this.from_date);
			}
			if (this.to_date) {
				params.set("to_date", this.to_date);
			}
			return params.toString();
		},

		listUrl() {
			return `/staffs/bankTransactions?${this.querySuffix()}`;
		},

		fetchTransactions(pageUrl) {
			if (!this.bank_account_id) {
				this.bankAccount = null;
				this.opening_balance = null;
				this.closing_balance = null;
				this.rows = [];
				this.pagination = [];
				return;
			}

			this.getToken();
			const url = pageUrl || this.listUrl();
			this.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const ok = res.data.status === "true" || res.data.status === true;
					if (!ok) {
						this.bankAccount = null;
						this.opening_balance = null;
						this.closing_balance = null;
						this.rows = [];
						this.pagination = [];
						return;
					}
					const payload = res.data.data || {};
					this.bankAccount = payload.bankAccount || null;
					this.opening_balance = payload.opening_balance != null ? payload.opening_balance : null;
					this.closing_balance = payload.closing_balance != null ? payload.closing_balance : null;
					const paginator = payload.bankTransactions;
					if (!paginator) {
						this.rows = [];
						this.pagination = [];
						return;
					}
					this.rows = paginator.data || [];
					this.pagination = paginator.links || [];
					this.from = paginator.from ?? 0;
					this.to = paginator.to ?? 0;
					this.total = paginator.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to load bank transactions", "error");
					this.bankAccount = null;
					this.opening_balance = null;
					this.closing_balance = null;
					this.rows = [];
					this.pagination = [];
				});
		},

		onPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchTransactions(`${base}${sep}${this.querySuffix()}`);
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		categoryTypeLabel(v) {
			const map = { 1: "Renewal/Service Payment", 2: "Vendor Payment", 3: "Courier Payment", 4: "Payment In", 5: "Payment Out", 6: "Contra", 7: "Other", 8: "Initial", 9:"Closing Update" };
			return map[Number(v)] || "—";
		},

		transactionTypeLabel(v) {
			const map = { 1: "Payment In", 2: "Payment Out" };
			return map[Number(v)] || "—";
		},

		transactionTypeClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-danger";
			return "bg-secondary";
		},

		openVerifyModal(row) {
			this.verifyModal = {
				open: true,
				row,
				payment_id: row.transaction_id != null && row.transaction_id !== "" ? String(row.transaction_id) : "",
				errors: {},
			};
		},

		closeVerifyModal() {
			this.verifyModal = {
				open: false,
				row: null,
				payment_id: "",
				errors: {},
			};
		},

		openRevokeModal(row) {
			if (!this.$canRevoke()) return;
			this.revokeModal.row = row;
			this.revokeModal.open = true;
			this.revokeModal.submitting = false;
		},

		closeRevokeModal() {
			this.revokeModal.open = false;
			this.revokeModal.row = null;
			this.revokeModal.submitting = false;
		},

		isApiSuccess(data) {
			return data?.status === true || data?.status === "true" || data?.success === true || data?.success === "true";
		},

		async confirmRevokeBankTransaction() {
			if (!this.revokeModal.row?.id) return;
			this.getToken();
			this.revokeModal.submitting = true;
			const formData = new FormData();
			formData.append("airo_bank_transaction_id", this.revokeModal.row.id);
			try {
				const res = await this.$axios.post("/staffs/revokeBankTransaction", formData, {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				});
				if (this.isApiSuccess(res.data)) {
					this.$swal("Success", this.extractMessage(res.data?.message) || "Transaction revoked", "success");
					this.closeRevokeModal();
					this.fetchTransactions(this.active_page_url || this.listUrl());
				} else {
					this.$swal("Error", this.extractMessage(res.data?.message) || "Failed to revoke transaction", "error");
				}
			} catch (err) {
				const data = err.response?.data || {};
				this.$swal("Error", this.extractMessage(data.message) || this.extractMessage(data.errors) || "Failed to revoke transaction", "error");
			} finally {
				this.revokeModal.submitting = false;
			}
		},

		submitVerify() {
			if (!this.verifyModal.row) return;
			this.verifyBankTransaction(this.verifyModal.row.id, this.verifyModal.payment_id);
		},

		verifyBankTransaction(bank_transaction_id, payment_id) {
			this.getToken();
			this.verifyingId = bank_transaction_id;
			const params = {
				bank_transaction_id: parseInt(bank_transaction_id, 10),
				payment_id: String(payment_id || "").trim() || null,
			};
			this.$axios
				.get("/staffs/verifyBankTransaction", {
					params,
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((response) => {
					const ok = response.data.status === true || response.data.status === "true";
					if (ok || response.status === 200) {
						this.$swal("Success", this.extractMessage(response.data.message) || "Transaction verified", "success");
						this.closeVerifyModal();
						this.fetchTransactions(this.active_page_url || this.listUrl());
					} else {
						this.applyFieldErrors(response.data.message);
						this.$swal("Error", this.extractMessage(response.data.message) || "Verification failed", "error");
					}
				})
				.catch((error) => {
					const data = error.response?.data || {};
					this.applyFieldErrors(data.errors);
					this.applyFieldErrors(data.message);
					this.$swal("Error", this.extractMessage(data.message) || this.extractMessage(data.errors) || "Something went wrong", "error");
				})
				.finally(() => {
					this.verifyingId = null;
				});
		},

		/** Backend may return message as a string or a validation map like { field: ["msg"] }. */
		extractMessage(message) {
			if (!message) return "";
			if (typeof message === "string") return message;
			if (Array.isArray(message)) return String(message[0] ?? "");
			if (typeof message === "object") {
				const first = Object.values(message)[0];
				if (Array.isArray(first)) return String(first[0] ?? "");
				return String(first ?? "");
			}
			return String(message);
		},

		applyFieldErrors(source) {
			if (!source || typeof source !== "object" || Array.isArray(source)) return;
			Object.keys(source).forEach((k) => {
				const v = source[k];
				this.verifyModal.errors[k] = Array.isArray(v) ? v[0] : v;
			});
		},
	},
};
</script>
